// components/LearningInsights.js — calm analytics surface, design-system primitives
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle, Clock, TrendingUp, TrendingDown, Minus, Play
} from 'lucide-react';
import { apiService, handleApiError } from '../services/api';
import Loading from './common/Loading';
import usePageTitle from '../hooks/usePageTitle';
import { Button, Card, Breadcrumbs, LevelDot } from './ui';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function prettifyConcept(key) {
  return (key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function relativeTime(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function formatHour(h) {
  if (h === null || h === undefined) return '—';
  const d = new Date();
  d.setUTCHours(h, 0, 0, 0);
  const local = d.getHours();
  const suffix = local >= 12 ? 'pm' : 'am';
  const h12 = local % 12 === 0 ? 12 : local % 12;
  return `${h12}${suffix}`;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatDaysSince(daysSince) {
  if (daysSince == null) return '—';
  if (daysSince === 0) return 'today';
  return `${daysSince}d`;
}

function hourBarClass({ count, isBest }) {
  if (count === 0) return 'bg-line-subtle';
  return isBest ? 'bg-status-good' : 'bg-accent';
}

function weekdayBgClass({ count, pct }) {
  if (count === 0) return 'bg-line-subtle';
  if (pct < 0.34) return 'bg-accent-soft';
  if (pct < 0.67) return 'bg-accent-subtle';
  return 'bg-accent';
}

// Mastery level → dot color (status token).
const LEVEL_DOT = {
  mastered: 'good', advanced: 'good', proficient: 'info',
  developing: 'warn', struggling: 'bad', novice: 'neutral',
};

// ---------------------------------------------------------------------------
// Section wrapper — neutral, just title + subtitle + body. No colored accents.
// ---------------------------------------------------------------------------
function Section({ title, subtitle, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      <div>
        <h2 className="text-lg font-semibold text-ink">{title}</h2>
        {subtitle && <p className="text-sm text-mutedink mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </motion.div>
  );
}

function EmptyHint({ message }) {
  return (
    <p className="text-sm text-mutedink italic">{message}</p>
  );
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

function NextActionsPanel({ items, courseId }) {
  if (!items.length) {
    return <EmptyHint message="Nothing urgent. Keep practising to surface priorities." />;
  }
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {items.map((a, idx) => (
        <Card key={`${a.concept}-${idx}`} className="p-4 flex flex-col">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-xs text-faint tabular-nums">{idx + 1}.</span>
                <span className="font-medium text-ink truncate">
                  {prettifyConcept(a.concept)}
                </span>
              </div>
              <div className="text-xs text-mutedink">{prettifyConcept(a.domain)}</div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <LevelDot color={LEVEL_DOT[a.mastery_level] || 'neutral'} />
              <span className="text-xs text-mutedink">{a.mastery_level}</span>
            </div>
          </div>
          <p className="text-sm text-read leading-relaxed flex-1 mb-3">
            {a.why}
          </p>
          <Button to={`/course/${courseId}/quiz/${a.domain}`} variant="primary" size="sm">
            <Play className="w-3 h-3" /> Practice
          </Button>
        </Card>
      ))}
    </div>
  );
}

function CadencePanel({ cadence }) {
  const activity = cadence?.activity || {};
  const byHour = activity.by_hour || {};
  const byWeekday = activity.by_weekday || {};
  const hourEntries = Array.from({ length: 24 }, (_, h) => byHour[h] || 0);
  const weekdayEntries = Array.from({ length: 7 }, (_, d) => byWeekday[d] || 0);
  const maxHour = Math.max(...hourEntries, 1);
  const maxDay = Math.max(...weekdayEntries, 1);
  const hasActivity = hourEntries.some(v => v > 0);

  const streak = cadence?.current_streak || 0;
  const sessionsThisWeek = activity.sessions_this_week || 0;
  const bestHour = activity.best_hour_utc;
  const bestHourAvg = activity.best_hour_avg_grade;
  const daysSince = cadence?.days_since_last_activity;

  // Inline stat strip — single style, no rainbow.
  const stats = [
    { n: streak,            label: streak === 1 ? 'day streak' : 'days streak' },
    { n: sessionsThisWeek,  label: 'days this week' },
    { n: formatHour(bestHour), label: bestHourAvg != null ? `best hour (${bestHourAvg.toFixed(1)}/10 avg)` : 'best hour' },
    { n: formatDaysSince(daysSince), label: 'last active' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        {stats.map(s => (
          <div key={s.label}>
            <span className="text-2xl font-bold text-ink tabular-nums">{s.n}</span>
            <span className="text-sm text-mutedink ml-2">{s.label}</span>
          </div>
        ))}
      </div>

      {hasActivity && (
        <div>
          <div className="text-xs text-faint uppercase tracking-wide mb-2">When you practice</div>
          <div className="flex items-end gap-0.5 h-16">
            {hourEntries.map((count, h) => {
              const heightPct = count === 0 ? 8 : 20 + (count / maxHour) * 80;
              const isBest = bestHour === h;
              const barClass = hourBarClass({ count, isBest });
              return (
                <div
                  key={formatHour(h)}
                  title={`${formatHour(h)}: ${count} attempts`}
                  className="flex-1 flex items-end"
                >
                  <div
                    className={`w-full rounded-sm transition-all ${barClass}`}
                    style={{ height: `${heightPct}%` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-faint mt-1">
            <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>12am</span>
          </div>
        </div>
      )}

      {hasActivity && (
        <div>
          <div className="text-xs text-faint uppercase tracking-wide mb-2">By day of week</div>
          <div className="grid grid-cols-7 gap-1">
            {weekdayEntries.map((count, d) => {
              const pct = count === 0 ? 0 : count / maxDay;
              const bg = weekdayBgClass({ count, pct });
              const text = pct >= 0.67 ? 'text-white' : 'text-read';
              return (
                <div
                  key={WEEKDAYS[d]}
                  title={`${WEEKDAYS[d]}: ${count} attempts`}
                  className={`${bg} rounded-md h-10 flex items-end justify-center transition-colors`}
                >
                  <span className={`text-[10px] font-medium ${text} mb-1`}>{WEEKDAYS[d]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!hasActivity && <EmptyHint message="No practice sessions recorded yet." />}
    </div>
  );
}

function MomentumPanel({ items }) {
  const meaningful = items.filter(m => m.trend !== 'no_data');
  if (!meaningful.length) {
    return <EmptyHint message="Need at least 4 attempts per domain to compute a trend." />;
  }
  const trendIcon = {
    improving: <TrendingUp   className="w-4 h-4 text-status-good" />,
    stable:    <Minus        className="w-4 h-4 text-faint" />,
    declining: <TrendingDown className="w-4 h-4 text-status-bad" />,
  };
  const trendLabel = {
    improving: 'Improving', stable: 'Stable', declining: 'Declining',
  };
  const trendText = {
    improving: 'text-status-good', stable: 'text-mutedink', declining: 'text-status-bad',
  };
  return (
    <div className="space-y-1">
      {items.map((m) => {
        const delta = m.delta;
        const deltaStr = delta == null
          ? ''
          : delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
        return (
          <div
            key={m.domain}
            className="flex items-center justify-between gap-3 py-2.5 border-b border-line-subtle last:border-0"
          >
            <div className="flex items-center gap-3 min-w-0">
              {trendIcon[m.trend] || trendIcon.stable}
              <div className="min-w-0">
                <div className="text-sm text-ink truncate">
                  {m.domain_name || prettifyConcept(m.domain)}
                </div>
                <div className="text-xs text-mutedink">
                  {m.trend === 'no_data'
                    ? `${m.sample_size} attempts — need ≥4`
                    : `${m.sample_size} recent attempts`}
                </div>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className={`text-sm font-medium ${trendText[m.trend] || trendText.stable}`}>
                {trendLabel[m.trend] || 'Stable'}
              </div>
              {m.trend !== 'no_data' && (
                <div className="text-xs text-mutedink tabular-nums">
                  {m.recent_avg?.toFixed(1)} vs {m.older_avg?.toFixed(1)} ({deltaStr})
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TimingPanel({ items }) {
  if (!items.length) {
    return <EmptyHint message="Answer a few more questions to see per-concept timing patterns." />;
  }
  const visible = items.slice(0, 10);
  const maxTime = Math.max(...visible.map(t => t.avg_time_s), 1);
  return (
    <div className="space-y-2">
      {visible.map((t, idx) => {
        const widthPct = Math.max(6, (t.avg_time_s / maxTime) * 100);
        const flagged = t.fast_wrong_count > 0;
        return (
          <div key={`${t.concept}-${idx}`} className="flex items-center gap-3">
            <div className="w-40 text-xs text-read truncate flex-shrink-0">
              {prettifyConcept(t.concept)}
            </div>
            <div className="flex-1 relative h-6 bg-line-subtle rounded overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 ${flagged ? 'bg-status-warn' : 'bg-accent'} transition-all duration-500`}
                style={{ width: `${widthPct}%` }}
              />
              <div className="absolute inset-0 flex items-center px-2 gap-2 text-[11px] font-medium">
                <span className={flagged ? 'text-status-warn' : 'text-white'}>{t.avg_time_s}s</span>
                <span className={flagged ? 'text-status-warn' : 'text-white/80'}>· {t.sample_size} attempts</span>
                {flagged && (
                  <span className="ml-auto text-status-warn">{t.fast_wrong_count}× fast-wrong</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <p className="text-xs text-faint mt-3">
        Fast-wrong = under 15s and incorrect. Often a sign of skimming.
      </p>
    </div>
  );
}

function MisconceptionsPanel({ items }) {
  if (!items.length) {
    return <EmptyHint message="No recurring wrong-answer patterns yet. Keep answering — patterns will surface here." />;
  }
  return (
    <div className="space-y-3">
      {items.map((m, idx) => (
        <Card key={`${m.concept}-${idx}`} className="p-4">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <LevelDot color="bad" />
              <span className="font-medium text-ink truncate">
                {prettifyConcept(m.concept)}
              </span>
              <span className="text-xs text-faint">{prettifyConcept(m.domain)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-mutedink">
              <span className="tabular-nums">{m.attempts_count}× wrong</span>
              <span className="text-faint">·</span>
              <span>{relativeTime(m.attempted_at)}</span>
            </div>
          </div>
          {m.question_text_snippet && (
            <p className="text-sm text-read mb-2 leading-relaxed">
              <span className="text-faint text-xs uppercase tracking-wide mr-2">Q</span>
              {m.question_text_snippet}
              {m.question_text_snippet.length >= 400 ? '…' : ''}
            </p>
          )}
          {m.wrong_option_text && (
            <p className="text-sm text-read bg-surface-muted border border-line-subtle rounded-md px-3 py-2">
              <span className="text-faint text-xs uppercase tracking-wide mr-2">You picked</span>
              {m.wrong_option_text}
            </p>
          )}
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function LearningInsights() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiService.getLearningInsights(courseId);
        setData(res.data.data);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [courseId]);

  usePageTitle(data?.course?.name ? `Insights — ${data.course.name}` : 'Insights');

  if (loading) return <Loading message="Crunching your learning signals..." />;

  if (error) {
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md px-6">
          <AlertTriangle className="w-10 h-10 text-status-warn mx-auto" />
          <p className="text-read">{error}</p>
          <Button onClick={() => navigate(-1)} variant="ghost">Go back</Button>
        </div>
      </div>
    );
  }

  const course = data?.course || {};
  const misconceptions = data?.misconceptions || [];
  const timing = data?.timing || [];
  const cadence = data?.cadence || {};
  const momentum = data?.momentum || [];
  const nextActions = data?.next_actions || [];

  const hasAnyData =
    misconceptions.length > 0 ||
    timing.length > 0 ||
    (cadence?.activity?.sessions_this_week || 0) > 0 ||
    momentum.some(m => m.trend !== 'no_data') ||
    nextActions.length > 0;

  return (
    <div className="min-h-screen bg-surface-muted">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-10">

        <Breadcrumbs items={[
          { label: 'My Courses', to: '/my-courses' },
          { label: course.name, to: `/course/${courseId}` },
          { label: 'Insights' },
        ]} />

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <p className="text-mutedink">The same patterns the AI uses to personalise your practice.</p>
        </motion.div>

        {!hasAnyData && (
          <Card className="p-10 text-center">
            <h2 className="text-lg font-medium text-ink mb-2">No signals yet</h2>
            <p className="text-mutedink text-sm max-w-md mx-auto">
              Answer a few questions and come back — patterns will surface here.
            </p>
          </Card>
        )}

        {hasAnyData && (
          <div className="space-y-10">
            <Section
              title="Do this next"
              subtitle="Top concepts to focus on, ranked"
            >
              <NextActionsPanel items={nextActions} courseId={courseId} />
            </Section>

            <Section
              title="Misconceptions"
              subtitle="Where you keep slipping"
            >
              <MisconceptionsPanel items={misconceptions} />
            </Section>

            <div className="grid gap-10 md:grid-cols-2">
              <Section
                title="Domain momentum"
                subtitle="Trend per domain over your last 20 attempts"
              >
                <MomentumPanel items={momentum} />
              </Section>

              <Section
                title="Timing"
                subtitle="Avg response time per concept"
              >
                <TimingPanel items={timing} />
              </Section>
            </div>

            <Section
              title="When you practice"
              subtitle="Streak, sessions, and best time of day"
            >
              <CadencePanel cadence={cadence} />
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
